import React, { useState, useEffect } from 'react';
import client from '../api/client';
import { FiPlus, FiTrash2, FiSave, FiEdit2 } from 'react-icons/fi';

function AccountSection() {
  const [profile, setProfile] = useState({ name: '', email: '', surgeryDate: '' });
  const [passwords, setPasswords] = useState({ currentPassword: '', newPassword: '' });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    client.get('/auth/me').then(res => {
      setProfile({
        name: res.data.name || '',
        email: res.data.email || '',
        surgeryDate: res.data.surgeryDate || '',
      });
    });
  }, []);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      const payload = { name: profile.name, surgeryDate: profile.surgeryDate || null };
      await client.put('/auth/me', payload);
      setMessage({ type: 'success', text: 'Profile updated' });
    } catch {
      setMessage({ type: 'error', text: 'Failed to update profile' });
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!passwords.currentPassword || !passwords.newPassword) return;
    setSaving(true);
    setMessage(null);
    try {
      await client.put('/auth/me', {
        currentPassword: passwords.currentPassword,
        newPassword: passwords.newPassword,
      });
      setPasswords({ currentPassword: '', newPassword: '' });
      setMessage({ type: 'success', text: 'Password changed' });
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to change password';
      setMessage({ type: 'error', text: msg });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="glass rounded-2xl p-6 space-y-6">
      <h2 className="text-lg font-semibold text-white">Account</h2>

      {message && (
        <div className={`text-sm px-3 py-2 rounded-lg ${
          message.type === 'success' ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'
        }`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSaveProfile} className="space-y-4">
        <div>
          <label className="block text-sm text-white/50 mb-1">Name</label>
          <input
            type="text"
            value={profile.name}
            onChange={e => setProfile(p => ({ ...p, name: e.target.value }))}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white placeholder-white/30 focus:border-white/30 focus:ring-0"
          />
        </div>
        <div>
          <label className="block text-sm text-white/50 mb-1">Email</label>
          <input
            type="email"
            value={profile.email}
            disabled
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white/40 cursor-not-allowed"
          />
        </div>
        <div>
          <label className="block text-sm text-white/50 mb-1">Surgery Date</label>
          <input
            type="date"
            value={profile.surgeryDate}
            onChange={e => setProfile(p => ({ ...p, surgeryDate: e.target.value }))}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white placeholder-white/30 focus:border-white/30 focus:ring-0"
          />
        </div>
        <button
          type="submit"
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition disabled:opacity-50 text-sm font-medium"
        >
          <FiSave size={14} />
          Save Profile
        </button>
      </form>

      <div className="border-t border-white/10 pt-6">
        <h3 className="text-sm font-semibold text-white/70 mb-4">Change Password</h3>
        <form onSubmit={handleChangePassword} className="space-y-4">
          <div>
            <label className="block text-sm text-white/50 mb-1">Current Password</label>
            <input
              type="password"
              value={passwords.currentPassword}
              onChange={e => setPasswords(p => ({ ...p, currentPassword: e.target.value }))}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white placeholder-white/30 focus:border-white/30 focus:ring-0"
            />
          </div>
          <div>
            <label className="block text-sm text-white/50 mb-1">New Password</label>
            <input
              type="password"
              value={passwords.newPassword}
              onChange={e => setPasswords(p => ({ ...p, newPassword: e.target.value }))}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white placeholder-white/30 focus:border-white/30 focus:ring-0"
            />
          </div>
          <button
            type="submit"
            disabled={saving || !passwords.currentPassword || !passwords.newPassword}
            className="flex items-center gap-2 px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition disabled:opacity-50 text-sm font-medium"
          >
            Change Password
          </button>
        </form>
      </div>
    </div>
  );
}

function MedicationsSection() {
  const [medications, setMedications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', dosage: '', schedule: 'MORNING', isSupplement: false });

  useEffect(() => {
    loadMedications();
  }, []);

  const loadMedications = async () => {
    try {
      const res = await client.get('/medications');
      setMedications(res.data);
    } catch { /* */ } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!addForm.name.trim()) return;
    try {
      const res = await client.post('/medications', addForm);
      setMedications(prev => [...prev, res.data]);
      setAddForm({ name: '', dosage: '', schedule: 'MORNING', isSupplement: false });
      setShowAdd(false);
    } catch { /* */ }
  };

  const handleDelete = async (id) => {
    try {
      await client.delete(`/medications/${id}`);
      setMedications(prev => prev.filter(m => m.id !== id));
    } catch { /* */ }
  };

  const startEdit = (med) => {
    setEditingId(med.id);
    setEditForm({ name: med.name, dosage: med.dosage || '', schedule: med.schedule, isSupplement: med.isSupplement });
  };

  const handleSaveEdit = async (id) => {
    try {
      const res = await client.put(`/medications/${id}`, editForm);
      setMedications(prev => prev.map(m => m.id === id ? res.data : m));
      setEditingId(null);
    } catch { /* */ }
  };

  const morning = medications.filter(m => m.schedule === 'MORNING');
  const evening = medications.filter(m => m.schedule === 'EVENING');
  const other = medications.filter(m => m.schedule !== 'MORNING' && m.schedule !== 'EVENING');

  const renderGroup = (title, meds) => {
    if (meds.length === 0) return null;
    return (
      <div className="space-y-2">
        <h3 className="text-xs font-semibold uppercase text-white/40 tracking-wide">{title}</h3>
        {meds.map(med => (
          <div key={med.id} className="flex items-center gap-3 bg-white/5 rounded-xl px-4 py-3">
            {editingId === med.id ? (
              <div className="flex-1 space-y-2">
                <input
                  type="text"
                  value={editForm.name}
                  onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full bg-white/10 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm focus:border-white/30 focus:ring-0"
                />
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={editForm.dosage}
                    onChange={e => setEditForm(f => ({ ...f, dosage: e.target.value }))}
                    placeholder="Dosage"
                    className="flex-1 bg-white/10 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm placeholder-white/30 focus:border-white/30 focus:ring-0"
                  />
                  <select
                    value={editForm.schedule}
                    onChange={e => setEditForm(f => ({ ...f, schedule: e.target.value }))}
                    className="bg-white/10 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm focus:border-white/30 focus:ring-0"
                  >
                    <option value="MORNING">Morning</option>
                    <option value="EVENING">Evening</option>
                  </select>
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 text-sm text-white/60">
                    <input
                      type="checkbox"
                      checked={editForm.isSupplement}
                      onChange={e => setEditForm(f => ({ ...f, isSupplement: e.target.checked }))}
                      className="rounded bg-white/10 border-white/20 text-white focus:ring-0"
                    />
                    Supplement
                  </label>
                  <button onClick={() => handleSaveEdit(med.id)} className="text-sm text-white/70 hover:text-white">Save</button>
                  <button onClick={() => setEditingId(null)} className="text-sm text-white/40 hover:text-white">Cancel</button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex-1">
                  <span className="text-white text-sm">{med.name}</span>
                  {med.dosage && <span className="text-white/40 text-sm ml-2">{med.dosage}</span>}
                  {med.isSupplement && <span className="text-xs text-white/30 ml-2">(supplement)</span>}
                </div>
                <button onClick={() => startEdit(med)} className="text-white/30 hover:text-white transition">
                  <FiEdit2 size={14} />
                </button>
                <button onClick={() => handleDelete(med.id)} className="text-white/30 hover:text-red-400 transition">
                  <FiTrash2 size={14} />
                </button>
              </>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="glass rounded-2xl p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white">Medications & Supplements</h2>
        <button
          onClick={() => setShowAdd(!showAdd)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 text-white rounded-lg hover:bg-white/20 transition text-sm"
        >
          <FiPlus size={14} />
          Add
        </button>
      </div>

      {showAdd && (
        <form onSubmit={handleAdd} className="bg-white/5 rounded-xl p-4 space-y-3">
          <input
            type="text"
            value={addForm.name}
            onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))}
            placeholder="Medication name"
            className="w-full bg-white/10 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-white/30 focus:border-white/30 focus:ring-0"
            autoFocus
          />
          <div className="flex gap-2">
            <input
              type="text"
              value={addForm.dosage}
              onChange={e => setAddForm(f => ({ ...f, dosage: e.target.value }))}
              placeholder="Dosage (optional)"
              className="flex-1 bg-white/10 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-white/30 focus:border-white/30 focus:ring-0"
            />
            <select
              value={addForm.schedule}
              onChange={e => setAddForm(f => ({ ...f, schedule: e.target.value }))}
              className="bg-white/10 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-white/30 focus:ring-0"
            >
              <option value="MORNING">Morning</option>
              <option value="EVENING">Evening</option>
            </select>
          </div>
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm text-white/60">
              <input
                type="checkbox"
                checked={addForm.isSupplement}
                onChange={e => setAddForm(f => ({ ...f, isSupplement: e.target.checked }))}
                className="rounded bg-white/10 border-white/20 text-white focus:ring-0"
              />
              Supplement
            </label>
            <div className="flex gap-2">
              <button type="button" onClick={() => setShowAdd(false)} className="text-sm text-white/40 hover:text-white px-3 py-1.5">Cancel</button>
              <button type="submit" className="text-sm text-white bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg transition">Add</button>
            </div>
          </div>
        </form>
      )}

      {loading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white" />
        </div>
      ) : medications.length === 0 ? (
        <p className="text-white/40 text-sm text-center py-4">No medications added yet</p>
      ) : (
        <div className="space-y-4">
          {renderGroup('Morning', morning)}
          {renderGroup('Evening', evening)}
          {renderGroup('Other', other)}
        </div>
      )}
    </div>
  );
}

function FoodTagsSection() {
  const [tags, setTags] = useState({ inflammatory: [], antiInflammatory: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    client.get('/meals/tags')
      .then(res => setTags(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const formatTag = (tag) => tag.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  if (loading) {
    return (
      <div className="glass rounded-2xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Food Tags</h2>
        <div className="flex justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white" />
        </div>
      </div>
    );
  }

  return (
    <div className="glass rounded-2xl p-6 space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-white">Food Tags</h2>
        <p className="text-white/50 text-sm mt-1">Tags used to categorize your meals during check-ins</p>
      </div>

      <div>
        <h3 className="text-xs font-semibold uppercase text-white/40 tracking-wide mb-2">Inflammatory</h3>
        <div className="flex flex-wrap gap-2">
          {tags.inflammatory.map(tag => (
            <span key={tag} className="text-sm px-3 py-1 bg-white/5 text-white/70 rounded-lg">
              {formatTag(tag)}
            </span>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-xs font-semibold uppercase text-white/40 tracking-wide mb-2">Anti-Inflammatory</h3>
        <div className="flex flex-wrap gap-2">
          {tags.antiInflammatory.map(tag => (
            <span key={tag} className="text-sm px-3 py-1 bg-white/5 text-white/70 rounded-lg">
              {formatTag(tag)}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function SettingsPage() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white drop-shadow-sm">Settings</h1>
        <p className="text-white/70 mt-1">Manage your account and preferences</p>
      </div>
      <AccountSection />
      <MedicationsSection />
      <FoodTagsSection />
    </div>
  );
}
